import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "../components/PublicShell";
import { LegalContentRenderer } from "../components/LegalContentRenderer";
import { useIsMobile } from "../hooks/useIsMobile";
import { C, font } from "../styles/tokens";
import { LEGAL_CONFIG } from "../config/legalConfig";
import { fetchPublicLegalDocument } from "../api/legal";

export function PrivacyPage() {
  const isMobile = useIsMobile();
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    let active = true;
    fetchPublicLegalDocument("privacy").then((data) => {
      if (active && data) {
        setDoc(data);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    document.title = doc?.title ? `${doc.title} - TickAndPass` : "Privacy Policy - TickAndPass";
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content =
      "Privacy Policy explaining how TickAndPass collects, uses, processes, and protects information.";
  }, [doc]);

  const effectiveDate = doc?.effectiveDate || LEGAL_CONFIG.effectiveDate;
  const lastUpdated = doc?.lastUpdated || LEGAL_CONFIG.privacyLastUpdated;
  const version = doc?.version || LEGAL_CONFIG.privacyVersion || "1.0";
  const title = doc?.title || "TickAndPass Privacy Policy";

  return (
    <PublicShell>
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          padding: isMobile ? "32px 16px 56px" : "56px 24px 80px",
          fontFamily: font,
        }}
      >
        <header
          style={{
            marginBottom: 36,
            borderBottom: `1px solid ${C.divider}`,
            paddingBottom: 24,
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? 28 : 38,
              fontWeight: 800,
              color: C.navy,
              marginBottom: 12,
              letterSpacing: "-0.5px",
              lineHeight: 1.2,
            }}
          >
            {title}
          </h1>
          <div
            style={{
              fontSize: 13,
              color: C.textLight,
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <span>
              <strong>Effective Date:</strong> {effectiveDate}
            </span>
            <span>
              <strong>Last Updated:</strong> {lastUpdated}
            </span>
            <span>
              <strong>Version:</strong> {version}
            </span>
          </div>
        </header>

        {doc?.content ? (
          <LegalContentRenderer content={doc.content} />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 32,
              fontSize: 15,
              color: C.textDark,
              lineHeight: 1.7,
            }}
          >
          <section>
            <h2 style={sectionHeadingStyle}>1. Introduction</h2>
            <p>
              {LEGAL_CONFIG.companyName} ("TickAndPass", "Platform", "we", "us",
              or "our") values your trust and is committed to protecting your
              privacy. This Privacy Policy describes how we collect, store, use,
              process, and protect your information when you access or use our
              digital credential verification platform, website, and services.
              For terms governing the use of our services, please view our{" "}
              <Link
                to="/terms-and-conditions"
                style={{ color: C.primary, fontWeight: 600 }}
              >
                Terms & Conditions
              </Link>
              .
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>2. Information We Collect</h2>
            <p>
              We collect information that you directly provide to us, as well as
              technical data generated automatically during your interactions
              with the Platform. We process only information necessary for
              identity verification, credential validation, billing, security,
              and platform operations.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>3. Account Information</h2>
            <p>
              When you create an account or complete your user profile, we
              collect details such as your full name, email address, phone
              number, unique User ID, account role (Professional, Academic
              Institution Officer, or Organisation Officer), and associated
              institution or organization name.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>4. Business Information</h2>
            <p>
              For Academic Institutions and Commercial Organisations registering
              on TickAndPass, we collect entity details including registered
              company/institution name, official business address, contact
              person details, billing address, and Goods and Services Tax (GST)
              registration numbers where applicable.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>5. Professional Information</h2>
            <p>
              For Professional users, we collect educational qualifications,
              degree titles, dates of study, professional experience, employment
              history, certificates, licenses, registration numbers, and related
              credential metadata submitted for profile creation or
              verification.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>6. Verification Information</h2>
            <p>
              To process credential verification requests, we collect specific
              verification data provided by professionals or requesting
              entities, including student enrollment numbers, certificate
              reference codes, verifying officer responses, verification
              statuses, and timestamped decision records.
            </p>
            <p style={{ marginTop: 10 }}>
              To confirm who you are at sign-up we use Cashfree's identity
              verification: an Aadhaar OTP or DigiLocker check for
              professionals, and a GSTIN or PAN check for institutions and
              organisations. From an Aadhaar check we keep the masked number
              (only its last four digits), the name and date of birth it
              returns, and a one-way keyed fingerprint of the number that only
              stops one Aadhaar from opening two accounts. We do not keep the
              full number, the address, the gender or the provider's full
              answer. From a business check we keep the GSTIN or PAN and the
              registered business details.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>7. Payment Information</h2>
            <p>
              When payments are processed on TickAndPass, we record order
              metadata including Payment ID, Provider Order ID, transaction
              reference, payment status, payment gateway provider (Cashfree
              Payments), payment method category (UPI, Card, Net Banking),
              transaction amount, currency (INR), and payment timestamp.
            </p>
            <div style={calloutBoxStyle}>
              <strong>Payment Security Note:</strong> TickAndPass does{" "}
              <em>NOT</em> store sensitive payment credentials such as
              Credit/Debit card CVV, UPI PINs, One-Time Passwords (OTPs), ATM
              PINs, or Internet Banking passwords. All sensitive payment
              authorization is securely handled directly by PCI-DSS compliant
              payment gateway (Cashfree Payments) according to their respective
              privacy and security policies.
            </div>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>8. Technical Information</h2>
            <p>
              When you access our Platform, our servers automatically collect
              technical information including IP address, browser type and
              version, device hardware specifications, operating system,
              referrer URL, login timestamps, application access logs, and
              security event logs.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>9. Documents</h2>
            <p>
              We collect digital copies of documents uploaded by users for
              credential verification, such as degree certificates, transcripts,
              mark sheets, professional licenses, or government identity
              documents provided for account verification purposes.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>10. How We Use Information</h2>
            <p>
              We process your information strictly for legitimate business and
              operational purposes, including:
            </p>
            <ul style={bulletListStyle}>
              <li>Managing user accounts and verifying identity;</li>
              <li>
                Executing credential verification requests with authorized
                issuing bodies;
              </li>
              <li>
                Processing subscription payments and single verification
                transaction fees;
              </li>
              <li>
                Generating tax invoices and dispatching confirmation
                notifications;
              </li>
              <li>
                Providing responsive customer support and technical assistance;
              </li>
              <li>
                Monitoring platform security, preventing fraud, and detecting
                suspicious activity;
              </li>
              <li>
                Troubleshooting technical defects and optimizing platform
                performance;
              </li>
              <li>
                Complying with statutory, tax, and legal obligations under
                Indian law.
              </li>
            </ul>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>11. Payment Processing</h2>
            <p>
              Payment transactions are executed using official payment gateway
              modal dialogs provided by Cashfree Payments. Backend server-side
              verification validates payment signatures before capturing orders
              to ensure transaction authenticity and prevent duplicate charges.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              12. Invoice and Email Processing
            </h2>
            <p>
              Once our server has confirmed a payment with the payment provider,
              it records the payment, creates the tax invoice and its PDF, and
              emails you a confirmation.
            </p>
            <p style={{ marginTop: 10 }}>
              The automated confirmation email sent to your registered address
              contains customer name, invoice number, payment reference ID,
              order reference ID, paid amount, transaction date, payment
              provider, and payment method details. It never contains sensitive
              authentication credentials.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              13. Business Customer Responsibilities
            </h2>
            <p>
              Academic Institutions and Organisation Officers using TickAndPass
              to submit or verify candidate, employee, or applicant information
              are responsible for ensuring they possess the necessary lawful
              basis, authority, or consent required by applicable law to share
              such data with TickAndPass.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>14. Sharing of Information</h2>
            <p>
              TickAndPass shares personal information strictly on a need-to-know
              basis. Verified profile data and credentials are shared only with
              verifiers, employers, or institutions that you explicitly
              authorize. We do <strong>NOT</strong> sell, rent, or trade your
              personal data to third parties for advertising or marketing
              purposes.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>15. Authentication</h2>
            <p>
              TickAndPass runs its own sign-in. Passwords are stored only as
              one-way hashes. A signed-in session uses short-lived tokens that
              our servers check on every request; the website keeps the token
              that renews a session in a secure cookie that page scripts cannot
              read. Password reset links and account codes are emailed to your
              registered address. The one-time passwords of Aadhaar and GST
              checks are sent by the government systems through our identity
              verification provider, and we do not keep them.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>16. Data Security</h2>
            <p>
              We implement robust technical and organizational security controls
              designed to safeguard your information against unauthorized
              access, loss, alteration, or disclosure. Security measures include
              TLS/HTTPS transport encryption, database encryption at rest,
              strict role-based access control (RBAC), environment secret
              isolation, and continuous security auditing.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>17. Data Retention</h2>
            <p>
              We retain personal data for as long as necessary to fulfill the
              purposes outlined in this policy, maintain active user accounts,
              meet tax and statutory invoice record requirements, resolve
              disputes, prevent fraud, and comply with applicable legal
              obligations. Notifications you have read are deleted 180 days
              after you read them.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              18. Account Deletion and Erasure
            </h2>
            <p>
              You can delete your account in the TickAndPass app (Settings, then
              Delete account). Deleting closes the account at once: you are
              signed out everywhere, nobody can sign in to it, and we stop
              sending it emails and notifications. Your information is kept, so
              that you can reactivate the account by signing in again with a
              code we email to you. The{" "}
              <Link
                to="/account-deletion"
                style={{ color: C.primary, fontWeight: 600 }}
              >
                account deletion page
              </Link>{" "}
              explains each step.
            </p>
            <p style={{ marginTop: 10 }}>
              To have your personal information erased, write to{" "}
              <a
                href={`mailto:${LEGAL_CONFIG.privacyEmail}`}
                style={{
                  color: C.primary,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                {LEGAL_CONFIG.privacyEmail}
              </a>{" "}
              from the email address on your account. We will close the account
              and delete your personal identifiers, except records we must keep
              by law (such as tax invoices), records needed for an active legal
              dispute, and records needed to prevent fraud.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>19. Data Access and Correction</h2>
            <p>
              You have the right to access, review, update, or correct
              inaccuracies in your personal data stored within your TickAndPass
              account. You can make updates directly through your account
              dashboard or by submitting a request to{" "}
              <a
                href={`mailto:${LEGAL_CONFIG.privacyEmail}`}
                style={{
                  color: C.primary,
                  textDecoration: "none",
                  fontWeight: 600,
                }}
              >
                {LEGAL_CONFIG.privacyEmail}
              </a>
              .
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              20. Cookies and Similar Technologies
            </h2>
            <p>
              TickAndPass uses essential cookies, local storage tokens, and
              session identifiers strictly required for user authentication,
              security verification, theme preferences, and system
              functionality. We do not deploy third-party cross-site tracking or
              advertising cookies.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>21. Children's Privacy</h2>
            <p>
              TickAndPass is designed for professionals, academic institution
              representatives, and enterprise organizations. Our Platform is not
              directed to children under 18 years of age, and we do not
              knowingly collect personal data from minors.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              22. International Data Processing
            </h2>
            <p>
              TickAndPass primarily hosts data within secure cloud data centers
              located in India. Where third-party service providers (such as
              notification, crash reporting or cloud infrastructure services)
              process data globally, such processing is subject to
              industry-standard data protection standards.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>23. Third-Party Services</h2>
            <p>
              Our Platform integrates with select third-party service providers
              essential for service operation:
            </p>
            <ul style={bulletListStyle}>
              <li>
                <strong>Cashfree:</strong> Payment gateway and checkout
                processing, and identity verification (Aadhaar OTP, DigiLocker,
                PAN and GSTIN checks);
              </li>
              <li>
                <strong>Firebase (Google):</strong> Notifications to the
                TickAndPass mobile app, and the app's crash reports (device
                model, system and app version, the screen and the error, not
                linked to your name);
              </li>
              <li>
                <strong>Email Providers:</strong> Automated transactional email
                and invoice delivery.
              </li>
            </ul>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>
              24. Data Breach and Security Incidents
            </h2>
            <p>
              TickAndPass maintains formal incident management procedures to
              detect, investigate, and remediate suspected data security
              incidents. In the event of a security breach affecting personal
              data, we will notify impacted users and statutory authorities in
              accordance with applicable legal requirements.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>25. Changes to Privacy Policy</h2>
            <p>
              We may revise this Privacy Policy periodically to reflect service
              updates or regulatory changes. When revisions are made, the "Last
              Updated" date at the top of this page will be updated. We
              encourage users to review this Privacy Policy regularly.
            </p>
          </section>

          <section>
            <h2 style={sectionHeadingStyle}>26. Contact Information</h2>
            <p>
              For privacy inquiries, data subject access requests, or security
              concerns, please contact our Data Protection Office:
            </p>
            <div style={contactCardStyle}>
              <div>
                <strong>{LEGAL_CONFIG.companyName}</strong>
              </div>
              <div>Address: {LEGAL_CONFIG.businessAddress}</div>
              <div>
                Privacy Officer Email:{" "}
                <a
                  href={`mailto:${LEGAL_CONFIG.privacyEmail}`}
                  style={{
                    color: C.primary,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  {LEGAL_CONFIG.privacyEmail}
                </a>
              </div>
              <div>
                General Support Email:{" "}
                <a
                  href={`mailto:${LEGAL_CONFIG.supportEmail}`}
                  style={{
                    color: C.primary,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  {LEGAL_CONFIG.supportEmail}
                </a>
              </div>
              <div>
                Terms & Conditions:{" "}
                <Link
                  to="/terms-and-conditions"
                  style={{
                    color: C.primary,
                    textDecoration: "none",
                    fontWeight: 600,
                  }}
                >
                  View Terms & Conditions
                </Link>
              </div>
            </div>
          </section>
        </div>
        )}
      </div>
    </PublicShell>
  );
}

const sectionHeadingStyle = {
  fontSize: 20,
  fontWeight: 700,
  color: C.navy,
  marginBottom: 10,
  letterSpacing: "-0.2px",
};

const calloutBoxStyle = {
  marginTop: 12,
  padding: "12px 16px",
  borderRadius: 6,
  background: C.amberLight,
  border: `1px solid ${C.amber}`,
  fontSize: 13,
  color: C.textDark,
  lineHeight: 1.5,
};

const bulletListStyle = {
  margin: "8px 0 0 20px",
  padding: 0,
  lineHeight: 1.8,
};

const contactCardStyle = {
  marginTop: 14,
  padding: 16,
  borderRadius: 8,
  background: C.bgMuted,
  border: `1px solid ${C.divider}`,
  fontSize: 14,
  display: "flex",
  flexDirection: "column",
  gap: 6,
};
